import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { ShoppingBag, Trash2, Loader2, ArrowRight, CreditCard, Smartphone, Apple, Lock } from "lucide-react";
import { toast } from "sonner";

const SAR = (n) => (n || 0).toLocaleString("en-US");

export default function Cart() {
    const nav = useNavigate();
    const { user, authAxios } = useAuth();
    const { items, remove, clear, total } = useCart();
    const [method, setMethod] = useState("card");
    const [loading, setLoading] = useState(false);

    const pay = async () => {
        if (!user) {
            nav(`/login?next=/cart`);
            return;
        }
        if (method !== "card") {
            toast.info("Mada, Sadad, and Apple Pay are coming to NourNet soon. Use Card for now.");
            return;
        }
        if (items.length === 0) {
            toast.error("Your cart is empty.");
            return;
        }
        setLoading(true);
        try {
            const payload = {
                items: items.map((d) => ({
                    domain: d.domain,
                    tld: d.tld || "." + d.domain.split(".").slice(1).join("."),
                    years: 1,
                })),
                origin_url: window.location.origin,
            };
            const { data } = await authAxios.post("/checkout/create", payload);
            window.location.href = data.url;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Checkout failed");
            setLoading(false);
        }
    };

    return (
        <main className="min-h-screen bg-limestone-100 pt-28 pb-24" data-testid="cart-page">
            <div className="asir-stripe h-[6px]" aria-hidden="true" />

            <div className="mx-auto max-w-[1320px] px-5 pt-12 lg:px-8">
                <div className="flex items-end justify-between">
                    <div>
                        <p className="eyebrow">Cart · Checkout</p>
                        <h1 className="section-headline mt-3" style={{ fontSize: "clamp(36px, 4.4vw, 56px)" }}>
                            Your cart <span className="text-madder-500">({items.length})</span>
                        </h1>
                    </div>
                </div>

                {items.length === 0 ? (
                    <div className="mt-14 rounded-card border-2 border-dashed border-ink-300 bg-white p-16 text-center">
                        <ShoppingBag className="mx-auto h-10 w-10 text-ink-400" />
                        <p className="mt-4 text-[18px] font-semibold text-ink-700">Your cart is empty.</p>
                        <Link to="/home-a#hero-search" className="mt-6 inline-flex h-[56px] items-center gap-2 rounded-btn bg-madder-500 px-6 text-[16px] font-bold text-white hover:bg-madder-600" data-testid="cart-empty-cta">
                            Search for a domain
                            <ArrowRight className="h-5 w-5 rtl-mirror" />
                        </Link>
                    </div>
                ) : (
                    <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12">
                        {/* LEFT: items */}
                        <div className="lg:col-span-7">
                            <div className="overflow-hidden rounded-card border-2 border-ink-200 bg-white" data-testid="cart-items">
                                {items.map((it) => (
                                    <div key={it.domain} className="flex items-center justify-between gap-4 border-b border-ink-100 px-6 py-5 last:border-b-0" data-testid={`cart-item-${it.domain}`}>
                                        <div>
                                            <p className="font-mono text-[18px] font-bold text-ink-800 num-ltr">{it.domain}</p>
                                            <p className="text-[13px] font-semibold text-ink-600">1 year · auto-renew on</p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <p className="font-mono text-[18px] font-bold tabular-nums num-ltr text-ink-800">{it.price_sar} SAR</p>
                                            <button onClick={() => remove(it.domain)} aria-label="Remove" className="inline-flex h-10 w-10 items-center justify-center rounded-btn border-2 border-ink-200 text-ink-600 hover:border-madder-500 hover:text-madder-500" data-testid={`cart-remove-${it.domain}`}>
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button onClick={clear} className="mt-4 text-[13px] font-bold uppercase tracking-wider text-ink-500 hover:text-madder-500" data-testid="cart-clear">Clear cart</button>
                        </div>

                        {/* RIGHT: payment */}
                        <aside className="lg:col-span-5 space-y-6">
                            {/* Payment method tabs */}
                            <div className="rounded-card border-2 border-ink-200 bg-white p-6" data-testid="cart-method-select">
                                <p className="text-[13px] font-bold uppercase tracking-[0.14em] text-ink-700 mb-3">Payment method</p>
                                <div className="grid grid-cols-2 gap-2">
                                    {[
                                        { id: "card", label: "Card", icon: CreditCard, ready: true },
                                        { id: "mada", label: "Mada", icon: Smartphone, ready: false },
                                        { id: "applepay", label: "Apple Pay", icon: Apple, ready: false },
                                        { id: "sadad", label: "Sadad", icon: Smartphone, ready: false },
                                    ].map((m) => (
                                        <button
                                            key={m.id}
                                            type="button"
                                            onClick={() => setMethod(m.id)}
                                            data-testid={`cart-method-${m.id}`}
                                            className={`relative inline-flex h-[56px] items-center justify-center gap-2 rounded-btn border-2 px-4 text-[14px] font-bold transition-colors duration-200 ${method === m.id ? "border-ink-800 bg-ink-800 text-white" : "border-ink-300 bg-white text-ink-800 hover:border-ink-800"}`}
                                        >
                                            <m.icon className="h-4 w-4" />
                                            {m.label}
                                            {!m.ready && <span className="absolute -top-2 end-1 rounded-[4px] bg-souq-400 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-ink-900">Soon</span>}
                                        </button>
                                    ))}
                                </div>
                                {method !== "card" && (
                                    <p className="mt-4 rounded-[8px] bg-mudbrick-100 p-3 text-[13px] font-medium text-ink-700">
                                        {method === "mada" && "Mada integration is launching Q3 2026. Use Card for now."}
                                        {method === "applepay" && "Apple Pay launches alongside our iOS app. Use Card for now."}
                                        {method === "sadad" && "Sadad bill-pay integration is in final review with SAMA. Use Card for now."}
                                    </p>
                                )}
                            </div>

                            <div className="rounded-card border-2 border-ink-800 bg-white p-6" data-testid="cart-summary">
                                <p className="text-[13px] font-bold uppercase tracking-[0.14em] text-ink-700 mb-3">Order total</p>
                                <div className="flex items-baseline justify-between">
                                    <span className="text-[14px] font-semibold text-ink-600">Subtotal</span>
                                    <span className="font-mono text-[18px] font-bold tabular-nums num-ltr text-ink-800">{SAR(total)} SAR</span>
                                </div>
                                <div className="flex items-baseline justify-between mt-2">
                                    <span className="text-[14px] font-semibold text-ink-600">VAT (15%)</span>
                                    <span className="font-mono text-[14px] font-semibold tabular-nums num-ltr text-ink-700">{SAR(Math.round(total * 0.15))} SAR</span>
                                </div>
                                <div className="mt-5 border-t-2 border-ink-200 pt-4 flex items-baseline justify-between">
                                    <span className="text-[15px] font-bold text-ink-800">Total today</span>
                                    <span className="font-display text-[32px] font-extrabold tabular-nums num-ltr text-madder-500">{SAR(Math.round(total * 1.15))} SAR</span>
                                </div>
                                <button
                                    onClick={pay}
                                    disabled={loading}
                                    data-testid="cart-pay-btn"
                                    className="mt-6 inline-flex h-[60px] w-full items-center justify-center gap-2 rounded-btn bg-madder-500 px-6 text-[17px] font-bold text-white shadow-madder transition-all duration-200 hover:bg-madder-600 active:translate-y-[1px] disabled:opacity-60"
                                >
                                    {loading ? <><Loader2 className="h-5 w-5 animate-spin" />Opening Stripe…</> : <><Lock className="h-4 w-4" />Pay with {method === "card" ? "card" : "card (selected above is coming soon)"}</>}
                                </button>
                                <p className="mt-3 text-center text-[12px] font-medium text-ink-500">
                                    Secure payment via Stripe · PDPL &amp; ZATCA compliant
                                </p>
                            </div>
                        </aside>
                    </div>
                )}
            </div>
        </main>
    );
}
